import { Controller } from "@hotwired/stimulus"

// "Quick copy" for the broker share-link buttons: save the rate quote via fetch
// (so no full-page navigation), then copy one of its two links to the clipboard
// inside the same click gesture and show a small confirmation.
//
// Saving happens at most once. The response carries BOTH of the saved quote's
// links, so clicking the other button copies from the cached payload rather than
// posting again — clicking around must never leave a trail of duplicate quotes.
// Editing any input invalidates the cache, because that is a different deal.
//
// Attached to the form. Each quick-copy button names the link it wants:
//   data-action="share-generator#copy" data-share-generator-link-param="max_ltv"
export default class extends Controller {
  static targets = ["feedback"]

  connect() {
    this.saved = null
    this.invalidate = () => { this.saved = null }
    this.element.addEventListener("input", this.invalidate)
    this.element.addEventListener("change", this.invalidate)
  }

  disconnect() {
    this.element.removeEventListener("input", this.invalidate)
    this.element.removeEventListener("change", this.invalidate)
  }

  async copy(event) {
    const link = event.params.link // "rate" | "max_ltv"
    const button = event.currentTarget

    // A Max-LTV link needs only the early inputs, so it skips the form's native
    // required-field validation the way its submit button's formnovalidate would.
    if (link !== "max_ltv" && !this.element.checkValidity()) {
      this.element.reportValidity()
      return
    }

    const original = button.textContent
    button.disabled = true
    button.textContent = "Saving…"

    try {
      const data = this.saved || (await this.save())
      if (!data) return

      const url = link === "max_ltv" ? data.max_ltv_url : data.rate_url
      if (!url) {
        // The server names the gap, so this can't drift out of step with the
        // fields that actually gate the rate link.
        const missing = (data.missing_pricing_inputs || []).join(", ")
        this.showError([`This quote has no rate yet${missing ? ` — add ${missing}` : ""}.`])
        return
      }

      await this.writeToClipboard(url)
      this.showSuccess(url, data.admin_url)
    } catch (error) {
      this.showError(["Something went wrong saving the quote."])
    } finally {
      button.disabled = false
      button.textContent = original
    }
  }

  // Posts the form once and caches the resulting links. Returns null after
  // rendering the server's errors.
  async save() {
    const response = await fetch(this.element.action, {
      method: "POST",
      headers: { Accept: "application/json" },
      body: new FormData(this.element) // includes the CSRF token
    })
    const data = await response.json()

    if (!response.ok) {
      this.showError(data.errors || ["Could not save the quote."])
      return null
    }

    this.saved = data
    return data
  }

  async writeToClipboard(url) {
    try {
      await navigator.clipboard.writeText(url)
    } catch (error) {
      // Clipboard blocked (e.g. lost user activation) — the link is still shown
      // in the confirmation for manual copying.
    }
  }

  showSuccess(publicUrl, adminUrl) {
    if (!this.hasFeedbackTarget) return
    this.feedbackTarget.className = "text-xs mt-2 text-success flex flex-wrap items-center gap-2 justify-end"
    this.feedbackTarget.innerHTML = ""

    const label = document.createElement("span")
    label.textContent = "✓ Link copied:"
    label.className = "font-medium"

    const link = document.createElement("a")
    link.href = adminUrl
    link.textContent = publicUrl
    link.className = "link link-primary font-mono truncate max-w-xs"
    link.target = "_blank"

    this.feedbackTarget.append(label, link)
  }

  showError(messages) {
    if (!this.hasFeedbackTarget) return
    this.feedbackTarget.className = "text-xs mt-2 text-error text-right"
    this.feedbackTarget.textContent = [].concat(messages).join(", ")
  }
}
